import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, X } from 'lucide-react'
import { Button, Checkbox, Modal } from '@/components/ui'
import { FileUpload, type FileUploadProps } from '@/components/ui/FileUpload'
import { useNotificationStore } from '@/stores/useNotificationStore'
import {
  blackoutImageToJpeg,
  defaultIdCardBoxes,
  isBlackoutType,
  readImageSize,
  scaleBoxes,
  type Box,
  type Size,
} from '@/lib/imageBlackout'

// EP-160 T160-104x (AC-12): the operator covers the religion and blood-group
// lines of an ID-card copy; the exported JPEG is what gets uploaded.

const MIN_BOX = 12
const KEY_STEP = 4

export interface IdCardBlackoutProps {
  file: File
  onConfirm: (blob: Blob) => void
  onCancel: () => void
}

interface EditableBox {
  id: number
  box: Box
}

function clampBox(box: Box, bounds: Size): Box {
  const width = Math.min(Math.max(box.width, MIN_BOX), bounds.width)
  const height = Math.min(Math.max(box.height, MIN_BOX), bounds.height)
  return {
    x: Math.min(Math.max(box.x, 0), bounds.width - width),
    y: Math.min(Math.max(box.y, 0), bounds.height - height),
    width,
    height,
  }
}

interface BlackoutBoxProps {
  box: Box
  bounds: Size
  label: string
  removeLabel: string
  onChange: (box: Box) => void
  onRemove: () => void
}

function BlackoutBox({ box, bounds, label, removeLabel, onChange, onRemove }: BlackoutBoxProps) {
  const drag = useRef<{ mode: 'move' | 'resize'; px: number; py: number; start: Box } | null>(null)

  const begin = (mode: 'move' | 'resize') => (e: PointerEvent<HTMLElement>) => {
    e.stopPropagation()
    drag.current = { mode, px: e.clientX, py: e.clientY, start: box }
    e.currentTarget.setPointerCapture?.(e.pointerId)
  }
  const move = (e: PointerEvent<HTMLElement>) => {
    const d = drag.current
    if (!d) return
    const dx = e.clientX - d.px
    const dy = e.clientY - d.py
    onChange(
      clampBox(
        d.mode === 'move'
          ? { ...d.start, x: d.start.x + dx, y: d.start.y + dy }
          : { ...d.start, width: d.start.width + dx, height: d.start.height + dy },
        bounds,
      ),
    )
  }
  const end = () => {
    drag.current = null
  }
  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    const arrows: Record<string, [number, number]> = {
      ArrowLeft: [-KEY_STEP, 0],
      ArrowRight: [KEY_STEP, 0],
      ArrowUp: [0, -KEY_STEP],
      ArrowDown: [0, KEY_STEP],
    }
    const delta = arrows[e.key]
    if (!delta) return
    e.preventDefault()
    onChange(
      clampBox(
        e.shiftKey
          ? { ...box, width: box.width + delta[0], height: box.height + delta[1] }
          : { ...box, x: box.x + delta[0], y: box.y + delta[1] },
        bounds,
      ),
    )
  }

  return (
    <div
      role="group"
      tabIndex={0}
      aria-label={label}
      data-testid="blackout-box"
      className="absolute cursor-move touch-none bg-black/80 outline outline-2 outline-primary"
      style={{ left: box.x, top: box.y, width: box.width, height: box.height }}
      onPointerDown={begin('move')}
      onPointerMove={move}
      onPointerUp={end}
      onKeyDown={onKeyDown}
    >
      <button
        type="button"
        aria-label={removeLabel}
        className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-error text-error-content"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={onRemove}
      >
        <X className="h-3 w-3" aria-hidden="true" />
      </button>
      <span
        aria-hidden="true"
        className="absolute -bottom-1 -right-1 h-4 w-4 cursor-nwse-resize rounded-sm bg-primary"
        onPointerDown={begin('resize')}
        onPointerMove={move}
        onPointerUp={end}
      />
    </div>
  )
}

export function IdCardBlackout({ file, onConfirm, onCancel }: IdCardBlackoutProps) {
  const { t } = useTranslation('leases')
  const { t: tc } = useTranslation('common')
  const imgRef = useRef<HTMLImageElement>(null)
  const nextId = useRef(0)
  const [natural, setNatural] = useState<Size | null>(null)
  const [measured, setMeasured] = useState<Size | null>(null)
  const [boxes, setBoxes] = useState<EditableBox[]>([])
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)

  const newBoxes = useCallback(
    (list: Box[]): EditableBox[] => list.map((box) => ({ id: nextId.current++, box })),
    [],
  )

  const previewUrl = useMemo(() => URL.createObjectURL(file), [file])
  useEffect(() => () => URL.revokeObjectURL(previewUrl), [previewUrl])

  useEffect(() => {
    let cancelled = false
    readImageSize(file)
      .then((size) => {
        if (cancelled) return
        setNatural(size)
        setBoxes(newBoxes(defaultIdCardBoxes(size)))
      })
      .catch(() => !cancelled && setFailed(true))
    return () => {
      cancelled = true
    }
  }, [file, newBoxes])

  // The displayed size changes with the window; the boxes live in image pixels
  // so a resize never moves them over the wrong part of the card.
  useLayoutEffect(() => {
    const measure = () => {
      const rect = imgRef.current?.getBoundingClientRect()
      setMeasured(rect && rect.width > 0 && rect.height > 0 ? { width: rect.width, height: rect.height } : null)
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [natural, previewUrl])

  const display = measured ?? natural
  const shown = natural && display ? scaleBoxes(boxes.map((b) => b.box), natural, display) : []

  const updateBox = (index: number, screenBox: Box) => {
    if (!natural || !display) return
    const [imageBox] = scaleBoxes([screenBox], display, natural)
    setBoxes((prev) => prev.map((b, i) => (i === index ? { ...b, box: imageBox } : b)))
  }

  const addBox = () => {
    if (!natural) return
    setBoxes((prev) => [
      ...prev,
      ...newBoxes([{ x: natural.width * 0.1, y: natural.height * 0.1, width: natural.width * 0.25, height: natural.height * 0.1 }]),
    ])
  }

  const confirm = async () => {
    setBusy(true)
    setFailed(false)
    try {
      onConfirm(await blackoutImageToJpeg(file, boxes.map((b) => b.box)))
    } catch {
      setFailed(true)
      setBusy(false)
    }
  }

  return (
    <Modal
      isOpen
      onClose={onCancel}
      title={t('id_copy.title')}
      size="2xl"
      closeOnOverlay={false}
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onCancel}>
            {tc('actions.cancel')}
          </Button>
          <Button type="button" loading={busy} disabled={!natural} onClick={confirm}>
            {t('id_copy.use_blacked_out')}
          </Button>
        </>
      }
    >
      <p className="mb-3 text-sm text-base-content/70">{t('id_copy.instructions')}</p>
      {failed && (
        <p role="alert" className="mb-3 text-sm text-error">
          {t('id_copy.failed')}
        </p>
      )}
      <div className="relative mx-auto w-fit max-w-full">
        <img ref={imgRef} src={previewUrl} alt={t('id_copy.preview_alt')} className="block max-h-[60vh] max-w-full select-none" draggable={false} />
        {display &&
          shown.map((box, i) => (
            <BlackoutBox
              key={boxes[i].id}
              box={box}
              bounds={display}
              label={t('id_copy.box_label', { number: i + 1 })}
              removeLabel={t('id_copy.remove_box', { number: i + 1 })}
              onChange={(b) => updateBox(i, b)}
              onRemove={() => setBoxes((prev) => prev.filter((_, j) => j !== i))}
            />
          ))}
      </div>
      <Button type="button" variant="outline" size="sm" className="mt-3" leftIcon={<Plus className="h-4 w-4" aria-hidden="true" />} onClick={addBox}>
        {t('id_copy.add_box')}
      </Button>
    </Modal>
  )
}

export interface IdCopyUploadProps extends Omit<FileUploadProps, 'onUpload'> {
  onUpload: (file: File) => Promise<string>
}

interface PendingBlackout {
  file: File
  resolve: (blacked: File) => void
  reject: (reason: Error) => void
}

/**
 * A FileUpload with the "this is a copy of an ID card" option. While it is
 * ticked, every picked image goes through the blackout editor and only the
 * blacked-out JPEG is passed to `onUpload`; the original never leaves the
 * browser. A field that takes documents (PDF, Word) cannot be blacked out, so
 * the option is disabled there with a hint.
 */
export function IdCopyUpload({ onUpload, accept = 'image/*', ...rest }: IdCopyUploadProps) {
  const { t } = useTranslation('leases')
  const addToast = useNotificationStore((s) => s.addToast)
  const [isIdCopy, setIsIdCopy] = useState(false)
  const [pending, setPending] = useState<PendingBlackout | null>(null)
  const takesImages = accept.includes('image')

  const handleUpload = async (file: File): Promise<string> => {
    if (!isIdCopy) return onUpload(file)
    if (!isBlackoutType(file.type)) {
      addToast({ type: 'error', message: t('id_copy.not_supported') })
      throw new Error('id_copy_not_supported')
    }
    const blacked = await new Promise<File>((resolve, reject) => setPending({ file, resolve, reject }))
    return onUpload(blacked)
  }

  const close = (settle: (p: PendingBlackout) => void) => {
    if (pending) settle(pending)
    setPending(null)
  }

  return (
    <div className="space-y-2">
      <FileUpload {...rest} accept={accept} onUpload={handleUpload} />
      <Checkbox
        size="sm"
        label={t('id_copy.checkbox')}
        checked={isIdCopy && takesImages}
        disabled={!takesImages}
        onChange={(e) => setIsIdCopy(e.target.checked)}
      />
      {!takesImages && <p className="text-xs text-base-content/60">{t('id_copy.images_only_hint')}</p>}
      {pending && (
        <IdCardBlackout
          file={pending.file}
          onCancel={() => close((p) => p.reject(new Error('id_copy_cancelled')))}
          onConfirm={(blob) =>
            close((p) =>
              p.resolve(new File([blob], p.file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' })),
            )
          }
        />
      )}
    </div>
  )
}
